
import React, { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Search, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle, Trash2, CheckCircle } from "lucide-react";
import { format } from "date-fns";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { logAttendanceScan } from "@/lib/attendanceScanLog";

export default function ASDRoster() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Check if user is EA Staff (read-only access)
  const isEAStaff = user?.role === 'user' && user?.user_type === 'ea_staff';
  const isAdmin = user?.role === 'admin';

  const { data: rosterEntries = [], isLoading } = useQuery({
    queryKey: ['rosterEntries'],
    queryFn: () => base44.entities.RosterEntry.list('-created_date'),
  });

  // Filter for ASD Roster - only entries moved to ASD (status === 'asd'), NOT excused
  const refusalEntries = rosterEntries.filter(e => e.status === 'asd');

  const filteredEntries = refusalEntries.filter(entry =>
    entry.student_first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.student_last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.teacher?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.course_short_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.other_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to get attendance priority for sorting
  const getAttendancePriority = (entry) => {
    // Check if excused first (status field)
    if (entry.status === 'excused') return 3;
    
    // Then check attendance_status
    const priorities = {
      'pending': 1,    // Most urgent - show first
      'absent': 2,
      'excused': 3,    // Backup if somehow in attendance_status
      'present': 4     // Least urgent - show last
    };
    return priorities[entry.attendance_status] || 5;
  };

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue, bValue;

    // Special handling for attendance sorting
    if (sortConfig.key === 'attendance') {
      aValue = getAttendancePriority(a);
      bValue = getAttendancePriority(b);
    } else {
      aValue = a[sortConfig.key];
      bValue = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convert to lowercase for string comparison if they are strings
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ArrowUp className="w-4 h-4 ml-1 text-blue-600" /> :
      <ArrowDown className="w-4 h-4 ml-1 text-blue-600" />;
  };

  // Count unique students (not assignments)
  const uniqueStudents = new Set(refusalEntries.map(e => e.other_id));
  const uniqueStudentCount = uniqueStudents.size;

  const deleteEntryMutation = useMutation({
    mutationFn: async (entryId) => {
      return base44.entities.RosterEntry.delete(entryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['rosterEntries']);
      setMessage({ type: 'success', text: 'ASD entry deleted successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: err.message || 'Failed to delete entry' });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const handleDelete = (entry) => {
    if (confirm(`Delete this ASD entry?\n\nStudent: ${entry.student_first_name} ${entry.student_last_name}\nTeacher: ${entry.teacher}\nCourse: ${entry.course_short_description}\n\nThis will permanently remove this specific assignment from the ASD roster.`)) {
      deleteEntryMutation.mutate(entry.id);
    }
  };

  const updateAttendanceMutation = useMutation({
    mutationFn: async ({ entryId, newStatus }) => {
      const entry = rosterEntries.find(e => e.id === entryId);
      if (!entry) return; // Return 0 if entry not found

      // All entries for this student that are currently on the ASD roster.
      // We used to also grab prior "excused" entries; that path is gone
      // because E is now stored on attendance_status while status stays 'asd'.
      const studentEntries = rosterEntries.filter(
        (e) => e.other_id === entry.other_id && e.status === 'asd'
      );

      // Update all entries for this student. E, P, A, R, Pending all live on
      // `attendance_status`; `status` STAYS 'asd' until closeout removes them.
      // Ps and Es are only cleared from the ASD roster during closeout, never
      // on mark.
      for (const studentEntry of studentEntries) {
        await base44.entities.RosterEntry.update(studentEntry.id, {
          attendance_status: newStatus,
          status: 'asd',
        });

        // Log the manual change for each entry
        await base44.entities.AttendanceLog.create({
          student_id: studentEntry.other_id,
          student_name: `${studentEntry.student_first_name} ${studentEntry.student_last_name}`,
          roster_entry_id: studentEntry.id,
          log_date: format(new Date(), 'yyyy-MM-dd'),
          status: newStatus,
          scanned: false,
          method: 'manual_edit'
        });
        logAttendanceScan({
          studentId: studentEntry.other_id,
          studentName: `${studentEntry.student_first_name} ${studentEntry.student_last_name}`,
          rosterType: 'asd',
          status: newStatus,
          method: 'manual_entry',
        });
      }

      return studentEntries.length; // Return the count of updated entries
    },
    onSuccess: (count) => { // Modified onSuccess to receive count
      queryClient.invalidateQueries(['rosterEntries']);
    },
  });

  const getAttendanceBadge = (status, rosterStatus) => {
    // E is now stored on attendance_status while status remains 'asd' until
    // closeout — Ps and Es must not disappear from the ASD roster on mark.
    const badges = {
      present: { color: 'bg-green-500 text-white font-bold', label: 'P', value: 'present' },
      absent: { color: 'bg-yellow-500 text-white font-bold', label: 'A', value: 'absent' },
      refusal: { color: 'bg-red-500 text-white font-bold', label: 'R', value: 'refusal' },
      excused: { color: 'bg-orange-500 text-white font-bold', label: 'E', value: 'excused' },
      pending: { color: 'bg-slate-300 text-slate-700 font-bold', label: 'Pending', value: 'pending' }
    };
    return badges[status] || badges.pending;
  };

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-full mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Current ASD Roster</h1>
          <p className="text-slate-600 mt-1">After School Detention Assignments ({sortedEntries.length} Entries)</p>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg bg-gradient-to-br from-red-500 to-red-600 text-white">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium opacity-90">Total Students in ASD</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{uniqueStudentCount}</div>
            <p className="text-xs mt-2 opacity-75">{refusalEntries.length} total assignments</p>
          </CardContent>
        </Card>

        {isEAStaff && (
          <Alert className="bg-blue-50 border-blue-200">
            <AlertCircle className="w-4 h-4 text-blue-600" />
            <AlertDescription className="text-blue-900">
              <strong>Read-Only Mode:</strong> You can view and search the roster but cannot modify attendance. Contact an administrator for attendance changes.
            </AlertDescription>
          </Alert>
        )}

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name, Other ID, teacher, or course..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isAdmin && <TableHead className="w-[80px]">Actions</TableHead>}
                    <TableHead 
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('attendance')}
                    >
                      <div className="flex items-center">
                        Attendance
                        {getSortIcon('attendance')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[100px]">Campus</TableHead>
                    <TableHead
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_last_name')}
                    >
                      <div className="flex items-center">
                        Student Last Name
                        {getSortIcon('student_last_name')}
                      </div>
                    </TableHead>
                    <TableHead
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('student_first_name')}
                    >
                      <div className="flex items-center">
                        Student First Name
                        {getSortIcon('student_first_name')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[100px]">Other ID</TableHead>
                    <TableHead className="min-w-[100px]">Student ID</TableHead>
                    <TableHead className="min-w-[120px]">Method of Instruction</TableHead>
                    <TableHead
                      className="min-w-[80px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('grade_level')}
                    >
                      <div className="flex items-center">
                        Grade Level
                        {getSortIcon('grade_level')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[120px]">Course</TableHead>
                    <TableHead className="min-w-[80px]">Section</TableHead>
                    <TableHead className="min-w-[150px]">Course Short Description</TableHead>
                    <TableHead
                      className="min-w-[120px] cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('teacher')}
                    >
                      <div className="flex items-center">
                        Teacher
                        {getSortIcon('teacher')}
                      </div>
                    </TableHead>
                    <TableHead className="min-w-[150px]">Assignment</TableHead>
                    <TableHead className="min-w-[100px]">Assignment Grade</TableHead>
                    <TableHead className="min-w-[100px]">Athletic Grade</TableHead>
                    <TableHead className="min-w-[150px]">Comments</TableHead>
                    <TableHead className="min-w-[100px]">Date</TableHead>
                    <TableHead className="min-w-[80px]">Time</TableHead>
                    <TableHead className="min-w-[120px]">F1G1 Parent Phone</TableHead>
                    <TableHead className="min-w-[120px]">F1G2 Parent Phone</TableHead>
                    <TableHead className="min-w-[150px]">Student Email</TableHead>
                    <TableHead className="min-w-[150px]">F1G1 Parent Email</TableHead>
                    <TableHead className="min-w-[120px]">F2G1 Parent Phone</TableHead>
                    <TableHead className="min-w-[150px]">F2G1 Parent Email</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 25 : 24} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 25 : 24} className="text-center py-8 text-slate-500">
                        No ASD students found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedEntries.map((entry) => {
                      const badge = getAttendanceBadge(entry.attendance_status, entry.status);
                      return (
                        <TableRow key={entry.id} className="hover:bg-slate-50">
                          {isAdmin && (
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(entry)}
                                disabled={deleteEntryMutation.isPending}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          )}
                          <TableCell>
                            {isEAStaff ? (
                              <Badge className={badge.color}>
                                {badge.label}
                              </Badge>
                            ) : (
                              <Select
                                value={badge.value}
                                onValueChange={(newStatus) =>
                                  updateAttendanceMutation.mutate({ entryId: entry.id, newStatus })
                                }
                              >
                                <SelectTrigger className={`w-28 ${badge.color}`}>
                                  <SelectValue>{badge.label}</SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="present">
                                    <span className="font-bold text-green-700">P - Present</span>
                                  </SelectItem>
                                  <SelectItem value="excused">
                                    <span className="font-bold text-orange-700">E - Excused</span>
                                  </SelectItem>
                                  <SelectItem value="absent">
                                    <span className="font-bold text-yellow-700">A - Absent</span>
                                  </SelectItem>
                                  <SelectItem value="refusal">
                                    <span className="font-bold text-red-700">R - Refusal</span>
                                  </SelectItem>
                                  <SelectItem value="pending">
                                    <span className="font-bold text-slate-700">Pending</span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                          </TableCell>
                          <TableCell>{entry.campus || '-'}</TableCell>
                          <TableCell className="font-medium">{entry.student_last_name}</TableCell>
                          <TableCell className="font-medium">{entry.student_first_name}</TableCell>
                          <TableCell className="font-mono text-sm font-bold">{entry.other_id}</TableCell>
                          <TableCell className="font-mono text-sm">{entry.student_id || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.method_of_instruction || '-'}</TableCell>
                          <TableCell>{entry.grade_level || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.course || '-'}</TableCell>
                          <TableCell>{entry.section || '-'}</TableCell>
                          <TableCell>{entry.course_short_description || '-'}</TableCell>
                          <TableCell>{entry.teacher}</TableCell>
                          <TableCell className="text-sm">{entry.assignment || '-'}</TableCell>
                          <TableCell>
                            <span className="font-semibold text-red-600">{entry.assignment_grade || '-'}</span>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold text-red-600">{entry.athletic_grade || '-'}</span>
                          </TableCell>
                          <TableCell className="text-sm">{entry.comments || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.date || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.time || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f1g1_parent_phone || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f1g2_parent_phone || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.student_email || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.f1g1_parent_email || '-'}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.f2g1_parent_phone || '-'}</TableCell>
                          <TableCell className="text-sm">{entry.f2g1_parent_email || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
